import Data from './Data';
import Config from '../Models/Config';
import Helper from '../Models/Helper';
import Pattern from '../Models/Pattern';
import Session from './Session';
import Trigger from '../Models/Trigger';
import {get, writable} from 'svelte/store';

let active = window.navigator.onLine;
if (typeof active === 'undefined') { /* Don't use Helper here, it's not initialized yet. */
	if (window.navigator.connection) {
		const type = window.navigator.connection.type;
		active = ![window.Connection.UNKNOWN, window.Connection.NONE].includes(type);
	}
	else active = true;
}

/*
 * plans is the number of active reading plans: null until asked, so the menu and
 * the start screen can tell "nothing to show" apart from "not known yet".
 */
const content = {cache: {}, busy: false, active, plans: null};
const api = writable(content);

api.busy = state => {
	content.busy = !!state;
	api.set(content);
};

/*
 * A write empties the whole GET cache, because most writes change what a
 * subsequent read would return. Pass `keep` for the ones that do not — see
 * api.markPlanPassage.
 */
api.request = async (method, uri, data, allow, keep) => {
	const token = get(Session).authentication.token;
	const payload = {method: method.toUpperCase(), headers: {}};
	if (token) payload.headers['Authorization'] = `Bearer ${token}`;
	if (Helper.is(data)) {
		payload.headers['Content-Type'] = 'application/json';
		payload.body = JSON.stringify(data);
	}

	let response;
	const read = method.toLowerCase() === 'get';
	const cache = !Helper.undefined(content.cache[uri]);
	if (read && cache) response = content.cache[uri];
	else try {
		if (read || token || allow) {
			const base = get(Data).version.api;
			const url = `${base}/${uri}`;
			api.busy(true);
			const message = await fetch(url, payload);
			api.busy(false);
			response = await message.json();
			if (read && response.success) content.cache[uri] = response;
			else if (!keep) content.cache = {};
			api.set(content);
		}
		else throw new Error();
	}
	catch (error)
		{ response = {success: false, data: error}; }

	return response;
};

api.read = async uri => {
	return await api.request('get', uri);
};

api.write = async (method, uri, data, allow, keep) => {
	return await api.request(method, uri, data, allow, keep);
};

/* User */

api.signUp = async (data = {mail, pass, name}) => {
	return await api.write('post', 'user', data, true);
};

api.signIn = async (data = {mail, pass}) => {
	const response = await api.write('post', 'user/session', data, true);
	if (response.success) {
		Session.updateAuthentication(response.token);
		Session.updateUser(response.data.name);
		Session.updateTheme(!response.data.dark);
		Session.updateBookmark(response.data.chapter);
		if (response.data.plans) Session.updatePlans(response.data.plans);
	}

	return response;
};

api.signOut = () => {
	Session.updateAuthentication(false);
};

api.verify = async (data = {verification}) => {
	return await api.write('post', 'user/verify', data, true);
};

api.forgot = async (data = {mail}) => {
	return await api.write('post', 'user/password/forgot', data, true);
};

api.verifyPassword = async (data = {pass, token}) => {
	return await api.write('post', 'user/password/verify', data, true);
};

api.updateUser = async data => {
	return await api.write('patch', 'user', data);
};

api.deleteUser = async () => {
	return await api.write('delete', 'user');
};

/* Favorite */

api.favorites = async () => {
	return await api.read('bookmarks');
};

api.insertFavorite = async (data = {verse}) => {
	return await api.write('post', 'bookmark', data);
};

api.updateFavorite = async (data = {verse, note}) => {
	return await api.write('patch', 'bookmark', data);
};

api.deleteFavorite = async (data = {verse}) => {
	return await api.write('delete', 'bookmark', data);
};

/* Plans */

api.plans = async () => {
	const response = await api.read('plans');

	/* A failed request keeps the previous answer, so a network hiccup does not
	 * make the reading plan entry disappear. */
	if (response.success && Array.isArray(response.plans)) {
		content.plans = response.plans.length;
		api.set(content);
	}

	return response;
};

api.plan = async code => {
	return await api.read(`plan/${code}`);
};

api.planProgress = async () => {
	return await api.read('plan/progress');
};

/*
 * Ticking a passage off keeps the cache: the plan itself does not change, and
 * progress is read from Session rather than from a GET. Dropping the cache here
 * meant re-fetching the 60-85 KB plan on the way back from every passage.
 */
api.markPlanPassage = async (data = {plan, passage}) => {
	return await api.write('post', 'plan/passage', data, false, true);
};

api.unmarkPlanPassage = async (data = {plan, passage}) => {
	return await api.write('delete', 'plan/passage', data, false, true);
};

/* Listeners */

Trigger.subscribe('bookmark', async state => {
	const data = {chapter: state.chapter};
	await api.updateUser(data);
});

Trigger.subscribe('theme', async state => {
	const data = {dark: !!state.dark};
	await api.updateUser(data);
});

Trigger.subscribe('plan_mark', async state => {
	await api.markPlanPassage({plan: state.plan, passage: state.passage});
});

Trigger.subscribe('plan_unmark', async state => {
	await api.unmarkPlanPassage({plan: state.plan, passage: state.passage});
});

window.addEventListener('offline', event => {
	content.active = false;
	api.set(content);
});

window.addEventListener('online', event => {
	content.active = true;
	api.set(content);
});

export default api;